import { useEffect, useState } from "react"
import { AdminGuard } from "../../auth/AdminGuard"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import {
  Settings,
  Database,
  Plug,
  Search,
  Layers,
  Shield,
  Users,
  Key,
  Mail,
  ListChecks,
  Sliders,
  FileText,
  BarChart3,
  Braces,
  Bug,
  RefreshCw,
  Plus,
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  Inbox,
} from "lucide-react"
import type { MenuItemsModel } from "../useMenuItemsModel"
import { groupCollectionsForMenu } from "../collectionMenuGrouping"
import { CollectionMenuRow } from "../CollectionMenuRow"
import { MenuItemsSearchPanel } from "../MenuItemsSearchPanel"
import { SHOW_FILES_MENU } from "../menuItemsConstants"
import { MenuSectionHeaderTile, MenuNavRowLeaf, menuNavIconSlotClass } from "../menuNavPrimitives"
import { isExactPath, isPathPrefix, isActiveCollectionRow } from "../menuNavActive"
import {
  collectionRowShellClasses,
  collectionRowPaddingComfortable,
  collectionRowLabelClasses,
  collectionRowIconClasses,
} from "../collectionNavRowClassNames"
import { SubmissionListEntryContent } from "../../public/submissionListEntryContent"

type MenuOpenSection = "admin" | "collections" | "submissions"

export function MenuLayoutSectioned({ m, mobile }: { m: MenuItemsModel; mobile: boolean }) {
  const pathname = m.pathname
  const [openSection, setOpenSection] = useState<MenuOpenSection | null>(() =>
    isPathPrefix(m.pathname, "/admin") ? "admin" : "collections"
  )
  const [folderOpenState, setFolderOpenState] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (isPathPrefix(pathname, "/admin")) {
      setOpenSection("admin")
    }
  }, [pathname])

  const toggleSection = (section: MenuOpenSection) => {
    setOpenSection((current) => (current === section ? null : section))
  }

  const adminOpen = openSection === "admin"
  const collectionsOpen = openSection === "collections"
  const submissionsOpen = openSection === "submissions"

  const {
    resetMenuKeyboardNav,
    registerMenuButton,
    showComponentsNav,
    isPendingSetup,
    componentsError,
    isLoading,
    filteredComponents,
    onCollectionMenuNavigate,
    isCollectionNavListButNoView,
    effectiveDebugMode,
    debugMode,
    toggleDebugMode,
    navCollectionLimits,
    navAdminComponents,
    navAdminEmail,
    navAdminPermissions,
    navAdminForms,
    navAdminJsonFeeds,
    navAdminUsers,
    navAdminGroups,
    navAdminTenants,
    navAdminMongo,
    navAdminSystem,
    navAdminConnectors,
    hasCollections,
    isAdmin,
    hasFormConfigAccess,
    navFiles,
    navMyCollections,
    navSubmissions,
    navActions,
    navInbox,
    hideInbox,
    hideActions,
    hideSubmissions,
    inboxCount,
    navCreateCollection,
    canCreateCollection,
    collectionRouteContext,
    handleReloadData,
    handleRetryCollections,
    collectionSelectedIndex,
    mongoCount,
    submissionMenuStatus,
    submissionMenuItems,
    navMySubmissionEntry,
  } = m

  const collectionsTint = "rounded-xl bg-blue-50/70 dark:bg-blue-950/25 pb-3 overflow-hidden"
  const submissionsBg = "bg-blue-50/70 dark:bg-blue-950/25"
  const adminBg = "bg-orange-50/65 dark:bg-orange-950/25"
  const submissionsTriggerTint = `${submissionsBg} rounded-t-xl overflow-hidden`
  const submissionsPanelTint = `${submissionsBg} rounded-b-xl pb-3 overflow-hidden w-full`
  const adminTriggerTint = `${adminBg} rounded-t-xl overflow-hidden`
  const adminPanelTint = `${adminBg} rounded-b-xl pb-3 overflow-hidden w-full`
  const accordionTriggerOpenClass =
    "bg-transparent hover:bg-transparent dark:hover:bg-transparent border-transparent rounded-b-none shadow-none"
  const menuGridClass = mobile
    ? "grid grid-cols-1 gap-2"
    : "grid grid-cols-1 gap-2 min-[2200px]:grid-cols-2"
  const menuGridCell = "min-w-0"

  const goCollectionManager = () => (isAdmin ? navAdminComponents : navMyCollections)()

  const collectionHeaderActions =
    showComponentsNav && !isPendingSetup ? (
      <div className="flex items-center gap-0.5 shrink-0">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            goCollectionManager()
          }}
          title="Collection manager"
          className="p-1.5 rounded-md text-gray-600 dark:text-gray-400 hover:bg-blue-100/80 dark:hover:bg-blue-900/50 hover:text-blue-800 dark:hover:text-blue-200"
        >
          <Settings className="w-4 h-4" />
        </button>
        {canCreateCollection ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              navCreateCollection()
            }}
            title="New collection"
            className="p-1.5 rounded-md text-gray-600 dark:text-gray-400 hover:bg-blue-100/80 dark:hover:bg-blue-900/50 hover:text-blue-800 dark:hover:text-blue-200"
          >
            <Plus className="w-4 h-4" />
          </button>
        ) : null}
      </div>
    ) : null

  const shellClass = mobile
    ? "w-full max-h-screen overflow-y-auto scrollbar-gutter-stable px-3 py-5 space-y-4"
    : "w-full h-full bg-white dark:bg-gray-800 flex flex-col"

  const innerScroll = mobile ? "" : "flex-1 min-h-0 p-3 flex flex-col overflow-y-auto scrollbar-gutter-stable"

  const renderCollectionsList = (menuIndexRef: { i: number }, compact: boolean) => {
    if (!showComponentsNav || isPendingSetup) return null
    if (componentsError) {
      return (
        <div className="py-2 space-y-2 text-center px-1">
          <p className="text-sm text-red-600 dark:text-red-400">Failed to load collections</p>
          <button
            type="button"
            onClick={handleRetryCollections}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            Retry
          </button>
        </div>
      )
    }
    if (isLoading) {
      return (
        <div className="py-2 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2 px-1">
          <Search className="w-4 h-4 animate-pulse" />
          Loading collections...
        </div>
      )
    }
    const { localFolders, connectorFolders } = groupCollectionsForMenu(filteredComponents)
    const folderSections = [
      ...localFolders.map((folder) => ({
        folderKey: folder.folderKey,
        folderLabel: folder.folderLabel,
        components: folder.components,
        remote: false,
      })),
      ...connectorFolders.map((folder) => ({
        folderKey: folder.folderKey,
        folderLabel: folder.folderLabel,
        components: folder.components,
        remote: true,
      })),
    ]

    if (folderSections.length === 0) {
      if (!m.searchTerm) {
        return (
          <div className="py-2 px-1 text-center text-sm text-gray-500 dark:text-gray-400">
            No collections available
          </div>
        )
      }
      return null
    }

    return (
      <div className="space-y-2">
        {folderSections.map(({ folderKey, folderLabel, components, remote }) => {
          const folderOpen = folderOpenState[folderKey] ?? true
          return (
            <div key={folderKey} className="space-y-1">
              <button
                type="button"
                onClick={() =>
                  setFolderOpenState((prev) => ({ ...prev, [folderKey]: !(prev[folderKey] ?? true) }))
                }
                className="w-full flex items-center justify-between rounded-md px-2 py-1 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <span className="min-w-0 truncate">{folderLabel}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  {remote ? (
                    <Plug className="w-3.5 h-3.5 shrink-0 text-violet-600 dark:text-violet-400" aria-hidden />
                  ) : null}
                  {folderOpen ? (
                    <ChevronDown className="w-3.5 h-3.5 shrink-0" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                  )}
                </span>
              </button>
              {folderOpen ? (
                <div className="space-y-1">
                  {components.map((component) => {
                    const currentIndex = ++menuIndexRef.i
                    const isKb = collectionSelectedIndex === currentIndex
                    const listNoView = isCollectionNavListButNoView(component)
                    const active = isActiveCollectionRow(pathname, component)
                    return (
                      <CollectionMenuRow
                        key={component.uid}
                        component={component}
                        active={active}
                        keyboardSelected={Boolean(isKb && !mobile && !active)}
                        listNoView={listNoView}
                        effectiveDebugMode={effectiveDebugMode}
                        compact={compact}
                        mobile={mobile}
                        menuIndex={currentIndex}
                        registerMenuButton={registerMenuButton}
                        onNavigate={onCollectionMenuNavigate}
                      />
                    )
                  })}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    )
  }

  const renderSubmissionsPanel = (menuIndexRef: { i: number }, compact: boolean) => {
    if (!showComponentsNav) return null

    return (
      <div className="space-y-1">
        {submissionMenuStatus === "loading" ? (
          <div className="py-2 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2 px-1">
            <Search className="w-4 h-4 animate-pulse" />
            Loading submissions...
          </div>
        ) : null}
        {submissionMenuStatus === "error" ? (
          <div className="py-2 px-1 text-center text-sm text-red-600 dark:text-red-400">
            Failed to load submissions
          </div>
        ) : null}
        {submissionMenuStatus === "ready" && submissionMenuItems.length > 0 ? (
          <div className="space-y-1">
            {submissionMenuItems.map((entry) => {
              const currentIndex = ++menuIndexRef.i
              const isKb = collectionSelectedIndex === currentIndex
              const archived = Boolean(entry.archived)
              const active = pathname === `/my-submissions/${entry.formToken}/${entry.uid}`
              return (
                <button
                  key={`${entry.formToken}-${entry.uid}`}
                  type="button"
                  ref={compact && !mobile ? registerMenuButton(currentIndex) : undefined}
                  onClick={() => navMySubmissionEntry(entry)}
                  title={archived ? "Archived — cannot open" : undefined}
                  className={`${collectionRowShellClasses({
                    routeActive: active,
                    keyboardSelected: Boolean(isKb && !mobile && !active),
                    listNoView: archived,
                    accent: "blue",
                  })} ${collectionRowPaddingComfortable}`}
                >
                  <div className="flex items-stretch gap-3 flex-1 min-w-0 w-full">
                    <span className={menuNavIconSlotClass}>
                      <FileText className={collectionRowIconClasses(active, true, "blue")} />
                    </span>
                    <div className="flex-1 min-w-0 text-left flex flex-col self-stretch">
                      <SubmissionListEntryContent
                        entry={entry}
                        variant="menu"
                        headerClassName={collectionRowLabelClasses(active, "blue")}
                      />
                    </div>
                    {archived ? (
                      <Badge
                        variant="outline"
                        className="text-[0.65rem] shrink-0 border-red-200 text-red-700 dark:border-red-800 dark:text-red-400"
                      >
                        Archived
                      </Badge>
                    ) : null}
                  </div>
                </button>
              )
            })}
          </div>
        ) : null}
      </div>
    )
  }

  const renderSubmissionsAdminRow = (menuIndexRef: { i: number }, compact: boolean) => {
    if (!showComponentsNav) return null
    if (hideSubmissions && !isAdmin) return null

    const gridClass = menuGridClass
    const gridCell = menuGridCell

    const submissionsTrigger = (
      <MenuNavRowLeaf
        Icon={FileText}
        label="Submissions"
        accent="blue"
        onClick={() => toggleSection("submissions")}
        ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
        className={
          submissionsOpen
            ? [accordionTriggerOpenClass, gridCell].filter(Boolean).join(" ")
            : gridCell
        }
        right={
          submissionsOpen ? (
            <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />
          )
        }
      />
    )

    const adminTrigger = (
      <MenuNavRowLeaf
        Icon={Shield}
        label="Administration"
        onClick={() => toggleSection("admin")}
        ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
        className={
          adminOpen
            ? [accordionTriggerOpenClass, gridCell].filter(Boolean).join(" ")
            : [gridCell, "hover:bg-orange-50/65 dark:hover:bg-orange-950/25"].join(" ")
        }
        right={
          adminOpen ? (
            <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />
          )
        }
      />
    )

    const submissionsCell = hideSubmissions ? null : (
      <div className={gridCell} key="submissions">
        <div className={submissionsOpen ? submissionsTriggerTint : undefined}>{submissionsTrigger}</div>
      </div>
    )

    const adminCell = (
      <AdminGuard silent key="admin">
        <div className={gridCell}>
          <div className={adminOpen ? adminTriggerTint : undefined}>{adminTrigger}</div>
        </div>
      </AdminGuard>
    )

    if (hideSubmissions) {
      if (adminOpen) {
        return (
          <div className="flex flex-col gap-2 min-w-0 w-full">
            <div className="flex flex-col gap-0">
              {adminCell}
              <div className={adminPanelTint}>{renderAdminPanel(menuIndexRef)}</div>
            </div>
          </div>
        )
      }
      return (
        <div className="flex flex-col gap-0 min-w-0 w-full">
          <div className={gridClass}>{adminCell}</div>
        </div>
      )
    }

    if (submissionsOpen) {
      return (
        <div className="flex flex-col gap-2 min-w-0 w-full">
          <div className="flex flex-col gap-0">
            {submissionsCell}
            <div className={submissionsPanelTint}>{renderSubmissionsPanel(menuIndexRef, compact)}</div>
          </div>
          {adminCell}
        </div>
      )
    }

    if (adminOpen) {
      return (
        <div className="flex flex-col gap-2 min-w-0 w-full">
          <div className="flex flex-col gap-0">
            {adminCell}
            <div className={adminPanelTint}>{renderAdminPanel(menuIndexRef)}</div>
          </div>
          {submissionsCell}
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-0 min-w-0 w-full">
        <div className={gridClass}>{[submissionsCell, adminCell]}</div>
      </div>
    )
  }

  const renderAdminPanel = (menuIndexRef: { i: number }) => (
    <div className="px-3 pt-2 space-y-3">
      <div>
        <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
          Access &amp; content
        </div>
        <div className="space-y-0.5">
          <MenuNavRowLeaf
            Icon={Sliders}
            label="Collection limits"
            compact
            accent="blue"
            onClick={navCollectionLimits}
            active={false}
            ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
          />
          <MenuNavRowLeaf
            Icon={Database}
            label="Manage collections"
            compact
            accent="blue"
            onClick={navAdminComponents}
            active={isPathPrefix(pathname, "/admin/collections")}
            ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
          />
          <MenuNavRowLeaf
            Icon={Key}
            label="Permissions"
            compact
            accent="blue"
            onClick={navAdminPermissions}
            active={isPathPrefix(pathname, "/admin/permissions")}
            ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
          />
          <MenuNavRowLeaf
            Icon={Braces}
            label="Mongo (raw)"
            compact
            accent="blue"
            onClick={navAdminMongo}
            active={isPathPrefix(pathname, "/admin/mongo")}
            ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
          />
          <MenuNavRowLeaf
            Icon={Settings}
            label="System"
            compact
            accent="blue"
            onClick={navAdminSystem}
            active={isPathPrefix(pathname, "/admin/system")}
            ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
          />
        </div>
      </div>
      <div>
        <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
          I/O
        </div>
        <div className="space-y-0.5">
          <MenuNavRowLeaf
            Icon={Plug}
            label="Connections"
            compact
            accent="blue"
            onClick={navAdminConnectors}
            active={isPathPrefix(pathname, "/admin/connectors")}
            ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
          />
          <MenuNavRowLeaf
            Icon={Settings}
            label="Public Forms"
            compact
            accent="blue"
            onClick={navAdminForms}
            active={isPathPrefix(pathname, "/admin/forms")}
            ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
          />
          <MenuNavRowLeaf
            Icon={BarChart3}
            label="Public JSON feeds"
            compact
            accent="blue"
            onClick={navAdminJsonFeeds}
            active={isPathPrefix(pathname, "/admin/json-feeds")}
            ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
          />
        </div>
      </div>
      <div>
        <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
          Users &amp; organizations
        </div>
        <div className="space-y-0.5">
          <MenuNavRowLeaf
            Icon={Users}
            label="Users"
            compact
            accent="blue"
            onClick={navAdminUsers}
            active={isPathPrefix(pathname, "/admin/users")}
            ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
          />
          <MenuNavRowLeaf
            Icon={Users}
            label="Groups"
            compact
            accent="blue"
            onClick={navAdminGroups}
            active={isPathPrefix(pathname, "/admin/groups")}
            ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
          />
          <MenuNavRowLeaf
            Icon={Database}
            label="Organizations"
            compact
            accent="blue"
            onClick={navAdminTenants}
            active={isPathPrefix(pathname, "/admin/tenants")}
            ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
          />
        </div>
      </div>
      <div>
        <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
          Delivery
        </div>
        <div className="space-y-0.5">
          <MenuNavRowLeaf
            Icon={Mail}
            label="Email"
            description="Logs, provider, test mail"
            compact
            accent="blue"
            onClick={navAdminEmail}
            active={isPathPrefix(pathname, "/admin/email")}
            ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
          />
        </div>
      </div>
      <div className="flex items-center justify-between rounded-md border-l-2 border-transparent py-1.5 hover:bg-gray-50/80 dark:hover:bg-gray-800/50">
        <div className="flex items-center gap-3">
          <span className={menuNavIconSlotClass}>
            <Bug className={`w-4 h-4 ${debugMode ? "text-yellow-600" : "text-gray-500"}`} />
          </span>
          <span className={`text-sm ${debugMode ? "font-medium text-yellow-900 dark:text-yellow-200" : "text-gray-700 dark:text-gray-300"}`}>
            Debug mode
          </span>
        </div>
        <Switch checked={debugMode} onCheckedChange={toggleDebugMode} />
      </div>
    </div>
  )

  const renderTopGrid = (menuIndexRef: { i: number }) => {
    if (!showComponentsNav) return null

    const gridClass = menuGridClass
    const gridCell = menuGridCell

    return (
      <div className={gridClass}>
        <MenuNavRowLeaf
          Icon={LayoutDashboard}
          label="Dashboard"
          onClick={navSubmissions}
          active={isExactPath(pathname, "/")}
          ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
          className={gridCell}
        />
        {hideInbox ? null : (
          <MenuNavRowLeaf
            Icon={Inbox}
            label="Inbox"
            onClick={navInbox}
            active={isExactPath(pathname, "/inbox")}
            ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
            className={gridCell}
            right={
              inboxCount > 0 ? (
                <Badge variant="secondary" className="text-[0.65rem] font-normal shrink-0">
                  {inboxCount}
                </Badge>
              ) : null
            }
          />
        )}
        {hasCollections && !hideActions ? (
          <MenuNavRowLeaf
            Icon={ListChecks}
            label="Actions"
            onClick={navActions}
            active={isPathPrefix(pathname, "/actions")}
            ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
            className={gridCell}
          />
        ) : null}
      </div>
    )
  }

  const renderTopSection = (menuIndexRef: { i: number }) => (
    <>
      {!isAdmin && hasFormConfigAccess ? (
        <MenuNavRowLeaf
          Icon={Settings}
          label="Public Forms"
          description="Form configuration"
          onClick={navAdminForms}
          active={isPathPrefix(pathname, "/admin/forms")}
          ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
        />
      ) : null}
      {collectionRouteContext ? (
        <MenuNavRowLeaf
          Icon={RefreshCw}
          label="Reload data"
          description={mobile ? undefined : "Current table"}
          onClick={handleReloadData}
          active={false}
          ref={!mobile ? registerMenuButton(++menuIndexRef.i) : undefined}
        />
      ) : null}
    </>
  )

  const renderCollectionsSection = (menuIndexRef: { i: number }, compact: boolean) => {
    if (!showComponentsNav || isPendingSetup) return null

    const collectionsSubtitle = `${filteredComponents.length} available${effectiveDebugMode && mongoCount > 0 ? ` · ${mongoCount} MongoDB` : ""
      }`

    return (
      <div className={`${collectionsTint} overflow-hidden`}>
        <MenuSectionHeaderTile
          Icon={Layers}
          title="Collections"
          subtitle={collectionsSubtitle}
          tileClassName="bg-blue-200/70 dark:bg-blue-900/45"
          iconClassName="text-blue-700 dark:text-blue-300"
          active={!collectionsOpen && Boolean(collectionRouteContext)}
          className={collectionsOpen ? accordionTriggerOpenClass : undefined}
          onClick={() => toggleSection("collections")}
          right={
            <>
              {collectionHeaderActions}
              {collectionsOpen ? (
                <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />
              )}
            </>
          }
        />
        {collectionsOpen ? renderCollectionsList(menuIndexRef, compact) : null}
      </div>
    )
  }

  resetMenuKeyboardNav()
  const menuIndexRef = { i: -1 }

  if (mobile) {
    return (
      <div className={shellClass}>
        <MenuItemsSearchPanel m={m} mobile />
        {renderTopGrid(menuIndexRef)}
        {renderSubmissionsAdminRow(menuIndexRef, false)}
        {renderCollectionsSection(menuIndexRef, false)}
        {renderTopSection(menuIndexRef)}
        {hasCollections && SHOW_FILES_MENU ? (
          <MenuNavRowLeaf Icon={FileText} label="Files" description="Uploaded files" onClick={navFiles} />
        ) : null}
      </div>
    )
  }

  return (
    <div className={shellClass}>
      <MenuItemsSearchPanel m={m} mobile={false} />
      <div className={innerScroll}>
        <div className="space-y-4">
          {renderTopGrid(menuIndexRef)}
          {renderSubmissionsAdminRow(menuIndexRef, true)}
          {renderCollectionsSection(menuIndexRef, true)}
          {renderTopSection(menuIndexRef)}
          {hasCollections && SHOW_FILES_MENU ? (
            <MenuNavRowLeaf
              Icon={FileText}
              label="Files"
              description="Uploaded files and links"
              onClick={navFiles}
              active={isPathPrefix(pathname, "/files")}
              ref={registerMenuButton(++menuIndexRef.i)}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}
